import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import Foldout from 'components/shared/Foldout';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import { RehabState } from 'models/flexible/RehabState';

import { translationsForRehabState } from './translationsHelper';

type Props = {
  open: boolean;
  handleClose: () => void;
  rehabState: RehabState;
  translations: Record<string, Record<string, string>>;
  onApply: (apiName: string, translations: Record<string, string>) => void;
};

export default function TranslationsSlideout({ open, handleClose, rehabState, translations, onApply }: Props) {
  const [localTranslations, setLocalTranslations] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setLocalTranslations(translations[rehabState.apiName] || {});
    }
  }, [open, translations, rehabState.apiName]);

  const handleCancel = () => {
    setLocalTranslations({});
    handleClose();
  };

  const handleApplyClick = () => {
    onApply(rehabState.apiName, localTranslations);
    handleClose();
  };

  const handleInputChange = (key: string, value: string) => {
    setLocalTranslations((prev) => ({ ...prev, [key]: value }));
  };

  const categories = useMemo(() => {
    return Object.entries(translationsForRehabState(rehabState.apiName));
  }, [rehabState]);

  return (
    <Foldout open={open} onBackdropClick={handleClose} width='min(80%, 480px)'>
      <SlideoutContainer>
        <SlideoutHeader>{rehabState.state} Overrides</SlideoutHeader>
        <SlideoutContent>
          {categories.map(([key, values], categoryIndex) => (
            <React.Fragment key={key}>
              <SectionHeader>{key}</SectionHeader>
              {(values as string[]).map((defaultString: string) => (
                <MappingRow key={defaultString}>
                  <InputGroup title='Default'>
                    <Input value={defaultString} disabled />
                  </InputGroup>
                  <Arrow>→</Arrow>
                  <InputGroup title='Override (optional)'>
                    <Input
                      value={localTranslations[defaultString] || ''}
                      onChange={(e) => handleInputChange(defaultString, e.target.value)}
                    />
                  </InputGroup>
                </MappingRow>
              ))}
              {categoryIndex < categories.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </SlideoutContent>
        <SlideoutFooter>
          <ButtonGroup>
            <Button variant='ghost' onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleApplyClick}>Apply</Button>
          </ButtonGroup>
        </SlideoutFooter>
      </SlideoutContainer>
    </Foldout>
  );
}

const SlideoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SlideoutHeader = styled.h2`
  font-size: 18px;
  font-weight: 700;
  padding: 24px;
  margin: 0;
  border-bottom: 1px solid var(--black-10);
`;

const SlideoutContent = styled.div`
  flex: 1;
  overflow: auto;
  padding: 24px;
`;

const SlideoutFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid var(--black-10);
`;

const MappingRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 8px;

  > *:first-child,
  > *:last-child {
    flex: 1;
  }
`;

const Arrow = styled.span`
  padding-bottom: 12px;
  color: var(--black-50);
`;

const Divider = styled.div`
  height: 1px;
  background-color: var(--black-10);
  margin: 20px 0;
`;
